import { useState, useEffect } from "react";
import { deadlineOverride } from "../lib/utils";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bookmark, CurrencyDollar, Calendar, Lightning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const Bookmarks = () => {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const res = await axios.get(`${API}/bookmarks`);
        setBookmarks(res.data.bookmarks || []);
      } catch (error) {
        console.error("Error fetching bookmarks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarks();
  }, []);

  const removeBookmark = async (grantId) => {
    try {
      await axios.delete(`${API}/bookmarks/${grantId}`);
      setBookmarks(prev => prev.filter(b => b.grant_id !== grantId));
      toast.success("Bookmark removed");
    } catch (error) {
      toast.error("Failed to remove bookmark");
    }
  };

  const formatDeadline = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "Expired";
    if (diffDays === 0) return "Due today";
    if (diffDays === 1) return "Due tomorrow";
    if (diffDays <= 7) return `${diffDays} days left`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bookmarks-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Bookmarked Grants</h1>
            <p className="text-zinc-400 mt-1">Grants you've saved for later</p>
          </div>
          <Button 
            onClick={() => navigate("/grants")}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="find-more-grants-btn"
          >
            Find More Grants
          </Button>
        </div>

        {/* Bookmarks Grid */}
        {bookmarks.length === 0 ? (
          <div className="text-center py-16">
            <Bookmark weight="duotone" className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No bookmarked grants</h3>
            <p className="text-zinc-500 mb-4">Save grants you're interested in to review later</p>
            <Button 
              onClick={() => navigate("/grants")}
              className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
              data-testid="browse-grants-btn"
            >
              Browse Grants
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookmarks.map((grant) => (
              <Card 
                key={grant.grant_id}
                className="bg-zinc-950 border-zinc-800 card-hover relative"
                data-testid={`bookmark-${grant.grant_id}`}
              >
                <CardContent className="p-6">
                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBookmark(grant.grant_id);
                    }}
                    className="absolute top-4 right-4 p-2 rounded-sm hover:bg-red-500/20 transition-colors group"
                    data-testid={`remove-bookmark-${grant.grant_id}`}
                  >
                    <Trash className="w-4 h-4 text-zinc-500 group-hover:text-red-400" />
                  </button>

                  {/* Category Badge */}
                  <span className="badge-emerald rounded-sm text-xs px-2 py-0.5 mb-3 inline-block">
                    {grant.category}
                  </span>

                  {/* Title */}
                  <h3 className="text-lg font-semibold mb-2 pr-8">{grant.title}</h3>
                  
                  {/* Description */}
                  <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
                    {grant.description}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className="flex items-center gap-1 text-emerald-400 font-mono">
                      <CurrencyDollar className="w-4 h-4" />
                      <span>${grant.amount_min.toLocaleString()} - ${grant.amount_max.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1 text-zinc-500">
                      <Calendar className="w-4 h-4" />
                      <span>{deadlineOverride(grant) || formatDeadline(grant.deadline)}</span>
                    </div>
                  </div>

                  {/* Apply Button */}
                  <Button 
                    className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                    onClick={() => navigate(`/grants/${grant.grant_id}`)}
                    data-testid={`apply-${grant.grant_id}`}
                  >
                    <Lightning className="w-4 h-4 mr-2" />
                    View & Apply
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Bookmarks;
